// src/Osint.jsx
import React from 'react';
import { useQuery } from 'react-query';
import { Box, Typography, useTheme } from "@mui/material";
import { tokens } from "../../theme";
import Header from '../../components/header/Header';
import GeneralDataGrid from '../../components/dataGrid/GeneralDataGrid';
import NetworkGraph from '../../components/network/Network';
import MapWithCircles from "../../components/geoMap/GeoMap";

import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import api from '../../services/api';
// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// OsintStats Component
const OsintStats = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  // Extract event_id and phone from URL
  const event_id = parseInt(window.location.pathname.split("/")[2], 10);
  const searchParams = new URLSearchParams(window.location.search);
  const phone = searchParams.get('phone') || 'N/A';

  // Fetch data using react-query
  const { data, isLoading, error } = useQuery(['osintStats', event_id], () =>
    api.get(`/api/osint/event/${event_id}/`).then(res => res.data)
  );

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error loading OSINT data.</div>;
  }


  // Find the index of the response that matches the phone number
const responseIndex = data.metadata.response.findIndex(
  (item) => item.response.target_msisdn === phone || item.response.target_msisdn === `+${phone}` || item.response.msisdn === phone || item.response.msisdn === `+${phone}`
);
  const report = data.metadata.response[responseIndex]?.response?.report;

if (!report || !report.analysis) {
  console.log(`invalid osint data structure for phone: +${phone}. responseIndex: ${responseIndex}`);
  return <div>Invalid OSINT data structure for phone: {phone}. responseIndex: {responseIndex}</div>;
}

  // Prepare DataGrid rows and columns for Incoming Numbers
  const rowsNumbersIncoming = report.analysis.numbers_incoming.map((number, index) => ({
    id: index + 1,
    number
  }));

  const columnsNumbersIncoming = [
    { field: "id", headerName: "ID", flex: 0.5 },
    {
      field: "number",
      headerName: "Incoming Number",
      flex: 1,
      cellClassName: "number-column--cell",
    },
  ];

  // Prepare DataGrid rows and columns for Outgoing Numbers
  const rowsNumbersOutgoing = report.analysis.numbers_outgoing.map((number, index) => ({
    id: index + 1,
    number
  }));

  const columnsNumbersOutgoing = [
    { field: "id", headerName: "ID", flex: 0.5 },
    {
      field: "number",
      headerName: "Outgoing Number",
      flex: 1,
      cellClassName: "number-column--cell",
    },
  ];

  // Prepare data for Frequency by Day chart
  const frequencyByDay = {
    labels: report.analysis.frequency_by_day.labels,
    datasets: [{
      label: report.analysis.frequency_by_day.datasets[0].label,
      data: report.analysis.frequency_by_day.datasets[0].data,
      backgroundColor: 'rgba(75, 192, 192, 0.6)',
    }],
  };

  // Prepare data for Frequency by Weekday chart
  const frequencyByWeekday = {
    labels: report.analysis.frequency_by_weekday.labels,
    datasets: [{
      label: report.analysis.frequency_by_weekday.datasets[0].label,
      data: report.analysis.frequency_by_weekday.datasets[0].data,
      backgroundColor: 'rgba(153, 102, 255, 0.6)',
    }],
  };

  // Prepare data for Frequency by Hour chart
  const frequencyByHour = {
    labels: report.analysis.frequency_by_hour.labels,
    datasets: [{
      label: report.analysis.frequency_by_hour.datasets[0].label,
      data: report.analysis.frequency_by_hour.datasets[0].data,
      backgroundColor: 'rgba(255, 159, 64, 0.6)',
    }],
  };

  // Define chart options
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: '',
      },
    },
  };

 
  return (
    <div className="osintStats">
      <h1>Osint Report {phone}</h1>
      <Box m="20px">
        <Header title="OSINT" subtitle="Top Target relationships" />
        <p className="graph-explanation">
  This graph visualizes the top incoming and outgoing calls for the target phone number. 
  Incoming calls are represented in blue, outgoing calls in red, and the central target is highlighted in orange.
</p>
        <NetworkGraph report={report} />
      </Box>
      {/* Incoming Numbers DataGrid */}
      <Box m="20px">
        <Header title="OSINT" subtitle="Incoming Numbers Data" />
        <GeneralDataGrid
          columns={columnsNumbersIncoming}
          rows={rowsNumbersIncoming}
          checkboxSelection
          sx={{
            "& .number-column--cell": {
              color: colors.greenAccent[300],
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.blueAccent[700],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[400],
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: colors.blueAccent[700],
            },
            "& .MuiCheckbox-root": {
              color: `${colors.greenAccent[200]} !important`,
            },
          }}
        />
      </Box>

      {/* Outgoing Numbers DataGrid */}
      <Box m="20px">
        <Header title="OSINT" subtitle="Outgoing Numbers Data" />
        <GeneralDataGrid
          columns={columnsNumbersOutgoing}
          rows={rowsNumbersOutgoing}
          checkboxSelection
          sx={{
            "& .number-column--cell": {
              color: colors.greenAccent[300],
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.blueAccent[700],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[400],
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: colors.blueAccent[700],
            },
            "& .MuiCheckbox-root": {
              color: `${colors.greenAccent[200]} !important`,
            },
          }}
        />
      </Box>

      {/* Charts Section */}
      <Box m="20px">
        <Header title="OSINT" subtitle="Frequency Analysis" />
        <Box display="flex" flexDirection="column" gap="40px">
          
          {/* Frequency by Day Chart */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Frequency by Day
            </Typography>
            <Bar 
              data={frequencyByDay} 
              options={{ 
                ...chartOptions, 
                plugins: { 
                  ...chartOptions.plugins, 
                  title: { 
                    display: true, 
                    text: 'Frequency by Day' 
                  } 
                } 
              }} 
            />
          </Box>
          
          {/* Frequency by Weekday Chart */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Frequency by Weekday
            </Typography>
            <Bar 
              data={frequencyByWeekday} 
              options={{ 
                ...chartOptions, 
                plugins: { 
                  ...chartOptions.plugins, 
                  title: { 
                    display: true, 
                    text: 'Frequency by Weekday' 
                  } 
                } 
              }} 
            />
          </Box>
          
          {/* Frequency by Hour Chart */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Frequency by Hour
            </Typography>
            <Bar 
              data={frequencyByHour} 
              options={{ 
                ...chartOptions, 
                plugins: { 
                  ...chartOptions.plugins, 
                  title: { 
                    display: true, 
                    text: 'Frequency by Hour' 
                  } 
                } 
              }} 
            />
          </Box>
          <box>
            {/* 
            "call_frequency_by_time_of_day": {
              "Morning": 5333,
              "Afternoon": 7473,
              "Evening": 6320,
              "Night": 1687
            }
            */}
            <Typography variant="h6" gutterBottom>
              Call Frequency by Time of Day
            </Typography>
            <Bar
              data={{
                labels: Object.keys(report.analysis.call_frequency_by_time_of_day),
                datasets: [{
                  label: 'Frequency',
                  data: Object.values(report.analysis.call_frequency_by_time_of_day),
                  backgroundColor: 'rgba(255, 99, 132, 0.6)',
                }],
              }}
              options={{
                ...chartOptions,
                plugins: {
                  ...chartOptions.plugins,
                  title: {
                    display: true,
                    text: 'Call Frequency by Time of Day',
                  },
                },
              }}
            />
          </box>
          {/* Additional Charts 
          
          "cluster_locations": [
              {
                "lat": 19.412952484349162,
                "lng": -99.26685972566737
              },
              {
                "lat": 19.266707922077924,
                "lng": -99.53113331168831
              },
              {
                "lat": 19.414066426332287,
                "lng": -99.17318756269593
              }
            ],
            // Show maps for each cluster location
          */}
          
            <Box display="flex" flexDirection="row" gap="20px">
              {report.analysis.cluster_locations.map((location, index) => (
                <Box key={index}>
                  <Typography variant="h6" gutterBottom>
                    Cluster {index + 1}
                  </Typography>
                  <div className="mapPlaceholder" style={{minWidth: "300px"}}>
                    <p>Longitude: {location.lng}</p><p>Latitude: {location.lat}</p><p><a href={`https://maps.google.com/?q=${location.lat},${location.lng}`} target='_blank'>Google maps</a></p>
                    <MapWithCircles lat={location.lat} lng={location.lng} radius2={1000} radius1={500} />
                  </div>
                </Box>
              ))}
            </Box>
        </Box>
      </Box>
    </div>
  );
};

export default OsintStats;